import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import type { ContactDTO, HabilitationDTO } from "@/features/api/habilitation/dto";
import { useHabilitationsAdminContentStore } from "./useHabilitationsAdminContentStore";
import type { Entite } from "./useHabilitationsAdminEngineStore";

export function useHabilitationAdd() {
  const store = useHabilitationAddStore()

  function showAddHabilitations() {
    return store.showDialog()
  }

  function $reset() {
    store.$reset()
  }

  return {
    showAddHabilitations,
    $reset
  }
}
export type DiagAjout = 'doublon' | "entreprise" | "etablissement"
export const useHabilitationAddStore = defineStore("habilitation-modale-add", () => {
  const storeContent = useHabilitationsAdminContentStore()
  const mode = computed(() => storeContent.engineState.mode)
  const estModeEntiteSirenSiret = computed(() => storeContent.estModeEntiteSirenSiret)
  const { habilitations } = storeToRefs(storeContent)

  const visible = ref(false)
  let returnDialogResult: ((value: HabilitationDTO | PromiseLike<HabilitationDTO>) => void) | null
  let throwCancel: ((value: any | PromiseLike<any>) => void) | null

  const $reset = () => {
    visible.value = false

    returnDialogResult = null
    throwCancel = null
  }

  const setResult = (data: HabilitationDTO) => {
    try {
      returnDialogResult?.call(null, data)
    } finally {
      $reset()
    }
  }

  const setCancel = () => {
    try {
      throwCancel?.call(null, "Traitement abandonné")
    } finally {
      $reset()
    }
  }

  const showDialog = () => {
    returnDialogResult = null
    throwCancel = null

    return new Promise<HabilitationDTO>((resolve, reject) => {
      visible.value = true
      returnDialogResult = resolve
      throwCancel = reject
    })
  }

  function diagContact(contact: ContactDTO | string): DiagAjout | undefined {
    const email = typeof contact === "string" ? contact.toLowerCase() : contact.email

    // recheche doublon
    const estDoublon = habilitations.value
      ?.map(h => h.contact ? h.contact.email.toLowerCase() : h.userId.toLowerCase())
      ?.some(emailHabilitation => emailHabilitation === email)
    if (estDoublon) {
      return 'doublon'
    }
  }

  function diagEntite(entite: Entite): DiagAjout | undefined {
    // recheche doublon
    const estDoublon = habilitations.value?.some(h => h.entite.siren === entite.siren && h.entite.nic === entite.nic)
    if (estDoublon) {
      return 'doublon'
    }
    if (entite.nic) {
      // recherche entreprise
      const estEntreprisePresente = habilitations.value
        ?.filter(h => h.entite.nic === null)
        ?.some(h => h.entite.siren === entite.siren)
      if (estEntreprisePresente) return "entreprise"
    } else {
      // recherche etablissement
      const estEtablissementPresent = habilitations.value
        ?.filter(h => h.entite.nic !== null)
        ?.some(h => h.entite.siren === entite.siren)
      if (estEtablissementPresent) return "etablissement"
    }
  }

  async function ajouterHabilitation(value: Entite | ContactDTO) {
    const habilitation = await storeContent.addHabilitation(value)
    if (habilitation) setResult(habilitation)
  }


  return {
    mode,
    estModeEntiteSirenSiret,
    visible,
    showDialog,
    ajouterHabilitation,
    diagEntite,
    diagContact,
    searchEntite: storeContent.searchEntite,
    searchContact: storeContent.searchContact,
    setResult,
    setCancel,
    $reset,
  }
})
